// src/components/product/ProductsTable.tsx
//
// ProductsTable
// - Renders list controls via StandardTableToolbar + StandardPagination.
// - Uses StandardTableFrame for loading/empty states.
// - Navigation policy: index-only navigation (consistent with other list pages).
//   - Index(#) click -> /products/:productId
// - Row status is derived from productStatus (warning/inactive/normal).

import React, { useCallback, useMemo } from 'react';
import { Box, Chip, Button, TableCell, TableRow, Typography } from '@mui/material';

import { toUsdDisplay } from '@utils/money';
import { ProductStatus } from '@models/product';
import {
  getProductSaleTypeMeta,
  getProductStatusMeta,
} from '@utils/productLabels';

import {
  StandardTableToolbar,
  StandardPagination,
  StandardTableFrame,
  TableRowStatus,
  cellSx,
} from '@components/common/table';
import { useListReturnNavigation } from '@hooks/useListReturnNavigation';

// TODO: Replace with real ProductResponse when available.
type ProductRow = any;

interface ProductsTableProps {
  data: ProductRow[];
  isLoading: boolean;
  totalCount?: number;
  page: number;
  pageSize: number;

  /**
   * Page change handler (page number only).
   * Keeping this simple avoids passing synthetic events across layers.
   */
  onChangePage: (nextPage: number) => void;
}

const ProductsTable = ({ data, isLoading, totalCount = 0, page, pageSize, onChangePage }: ProductsTableProps) => {
  const { navigateFromList } = useListReturnNavigation();
  const rows = useMemo(() => data ?? [], [data]);

  const handleOpenDetail = useCallback(
    (productId: number) => {
      if (!Number.isFinite(productId) || productId <= 0) return;
      navigateFromList(`/products/${productId}`);
    },
    [navigateFromList],
  );

  const getRowStatus = useCallback((row: ProductRow): TableRowStatus => {
    const statusValue = String(row.productStatus ?? '');
    if (statusValue === ProductStatus.OUT_OF_STOCK) return 'warning';
    if (statusValue === ProductStatus.DISCONTINUED) return 'inactive';
    return 'normal';
  }, []);

  // (# + Product Code + Product Name + Stock Code + Order Type + Price + Shipped(7d) + Shipped(total) + Refunded + Status)
  const columnsCount = 10;

  return (
    <Box>
      <StandardTableToolbar
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onChangePage={onChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<ProductRow>
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          rows={rows}
          getRowKey={(row) => String(row.productId)}
          getRowStatus={getRowStatus}
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell sx={cellSx.nowrap}>Product Code</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Product Name</TableCell>
              <TableCell width={140} align="center" sx={cellSx.nowrap}>
                Stock Code
              </TableCell>
              <TableCell width={140} align="center" sx={cellSx.nowrap}>
                Order Type
              </TableCell>
              <TableCell width={120} align="right" sx={cellSx.nowrap}>
                Price
              </TableCell>
              <TableCell width={170} align="right" sx={cellSx.nowrap}>
                Shipped (Last 7 days)
              </TableCell>
              <TableCell width={150} align="right" sx={cellSx.nowrap}>
                Shipped (Total)
              </TableCell>
              <TableCell width={120} align="right" sx={cellSx.nowrap}>
                Refunded
              </TableCell>
              <TableCell width={140} align="center" sx={cellSx.nowrap}>
                Status
              </TableCell>
            </TableRow>
          }
          renderRow={(row, index) => {
            const productId = Number(row.productId);
            const canNavigate = Number.isFinite(productId) && productId > 0;
            const saleTypeMeta = getProductSaleTypeMeta(row.productSaleType);
            const statusMeta = getProductStatusMeta(row.productStatus);

            return (
              <>
                {/* Index-only navigation (consistent list UX). */}
                <TableCell align="right" sx={cellSx.number}>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!canNavigate}
                    onClick={() => handleOpenDetail(productId)}
                    sx={{
                      minWidth: 0,
                      px: 0.5,
                      fontWeight: 800,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {(page - 1) * pageSize + index + 1}
                  </Button>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{row.productCode || '-'}</TableCell>

                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {row.productName || '-'}
                  </Typography>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip label={row.stock?.stockCode ?? '-'} size="small" variant="outlined" />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={saleTypeMeta.label}
                    color={saleTypeMeta.chip.color}
                    size="small"
                    variant={saleTypeMeta.chip.variant}
                  />
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {toUsdDisplay(Number(row.price ?? 0))}
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {Number(row.lastWeekShippedCount ?? 0).toLocaleString()}
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {Number(row.totalShippedCount ?? 0).toLocaleString()}
                </TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {Number(row.refundedCount ?? 0).toLocaleString()}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={statusMeta.label}
                    color={statusMeta.chip.color}
                    variant={statusMeta.chip.variant}
                    size="small"
                    sx={{ fontWeight: 800 }}
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onChangePage={onChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default ProductsTable;
